<template>
    <CardContainer>
        <div class="w-full">
            <template v-if="loadingCount>0">
                <SkeletonBase
                    hasTitle
                    :content="1"
                />
            </template>
            <template v-else>
                <h4 class="py-3 md:pb-3.5 md:pt-0">
                    請選擇{{ caseText }}
                </h4>
                <InputTypes
                    labelColor="text-gray-700"
                    :label="caseText"
                    :alignCenter="false"
                >
                    <template #input>
                        <Multiselect
                            v-model="selected"
                            label="label"
                            :options="dataList"
                            class="select-base max-w-[23.375rem] ml-0"
                            placeholder="請選擇"
                            :can-clear="false"
                            :can-deselect="false"
                            no-options-text="查無資料"
                        />
                    </template>
                    <template #button>
                        <ValidEmail
                            v-if="isEmailCase"
                            v-model:isValid="isValid"
                            v-model:needEmailVerify="needEmailVerify"
                            :email="selected"
                            class="mt-1 md:ml-2"
                        />
                    </template>
                </InputTypes>
            </template>
        </div>
        <ul class="text-gray-600 text-lg pb-6 border-b border-gray-200 mb-6">
            <li>申請本人所投保之所有有效及停效保單，以電子郵件寄送各項送金單或通知單，並取消紙本/單據寄發（包含日後以同一要保人向本公司投保生效之保險契約。</li>
            <li>本人同意向貴公司投保之所有保單的電子郵件信箱一併同時更新。</li>
            <li v-if="needEndorse">
                <br>
                <Checkbox
                    v-model:checkedValues="isEndorseAgree"
                    :value="'Y'"
                    :text="needEndorseDesc"
                    class="items-baseline mb-0 mr-0"
                />
            </li>
        </ul>
        <p class="text-error mb-4">
            為保障您的權益，請先詳讀線上保單服務約定條款、保單借款重要告知事項及電子通知單重要約定事項。
        </p>
        <TermsFrame
            height="h-auto"
            :content="term.content"
            :innerTitle="term.title"
        />
        <!-- 新客戶 -->
        <TermsFrame
            v-if="isNewCustom"
            class="mt-6"
            need-layer
            height="h-[15rem]"
            needGuideArrow
            :content="termNewCustom.content"
            :innerTitle="termNewCustom.title"
            @isScrollToBottom="v => isScrollToBottom = v"
        />

        <div class="flex flex-wrap xs:flex-nowrap xs:grid grid-cols-1 xs:grid-cols-2 sm:flex justify-center mt-6">
            <button
                class="btn btn-ghost border border-gray-400 rounded-full mb-3 mr-2 md:mr-4"
                @click="cancelApply"
            >
                取消申請
            </button>
            <button
                class="btn rounded-full mb-3 sm:px-6 ml-2 md:ml-4 gradient-primary"
                :disabled="!buttonDisabled"
                @click="submitData"
            >
                本人已閱讀並同意
            </button>
        </div>
        <Loading :isLoading="loadingCount>0" />
        <!-- 門號核實 -->
        <ModalMobileVerify
            v-model:postApiMid="postApiMid"
            @confirm="confirmApiMid"
        />
    </CardContainer>
</template>

<script>
import { ref, computed, inject } from 'vue';
import { useStore } from 'vuex';
import CardContainer from '@/components/card/CardContainer.vue';
import InputTypes from '@/components/inputs/InputTypes.vue';
import Multiselect from '@vueform/multiselect';
import TermsFrame from '@/components/template/TermsFrame.vue';
import { useRouter } from 'vue-router';
import routeName from '@/router/constant.js';
import { getInfoNotify, getAgreeTermsNotify, getOnlineAgreeTermsNotify, postAgreeNotify, postOnlineAgreeNotify, postSubmitNotify } from '@/apis/eNotifyPromo';
import { getNeedMIDVerify } from '@/apis/mid';
import ModalMobileVerify from '@/components/dialogs/ModalMobileVerify/ModalMobileVerify.vue';
import ValidEmail from '@/components/dialogs/ValidEmail.vue';
import SkeletonBase from '@/components/skeletons/SkeletonBase.vue';
import Checkbox from '@/components/checkboxs/Checkbox.vue';
import { postAgreeEndorse } from '@/apis/endorse.js';
import { maskMobile, maskEmail } from '@/utils/maskUtils.js';

export default {
    components: {
        InputTypes,
        Multiselect,
        TermsFrame,
        CardContainer,
        ModalMobileVerify,
        ValidEmail,
        SkeletonBase,
        Checkbox
    },
    setup() {
        const $vfm = inject('$vfm');
        const router = useRouter();
        const store = useStore();
        const selected = ref('');
        const loadingCount = ref(0);
        // memberType !== 'ONLINE' 都要顯示線上保單服務條款
        const isNewCustom = computed(() => store.state.eNotify.user?.enotifyPromo?.memberType !== 'ONLINE');
        // 條款閱讀完成至底部
        const isScrollToBottom = ref(false);
        const term = ref({});
        const termNewCustom = ref({});
        const dataList = ref([]);
        const isEmailCase = ref(true);
        const postApiMid = ref(false);
        const isValid = ref(false);
        const needEmailVerify = ref(true);
        // 保全批註說明文字
        const isEndorseAgree = ref('');
        const needEndorse = ref(false);
        const needEndorseDesc = ref('');
        // 依照身份別按鈕disabled條件不同
        const buttonDisabled = computed(() => {
            let isDisabled = true;
            if (isNewCustom.value) {
                isDisabled = selected.value && isScrollToBottom.value;
            } else {
                isDisabled = selected.value;
            }
            if (isEmailCase.value && needEmailVerify.value && !isValid.value) {
                isDisabled = false;
            }
            return !!isDisabled;
        });

        const caseText = computed(() => {
            return isEmailCase.value ? '電子郵件信箱' : '行動電話';
        });

        const getData = async () => {
            loadingCount.value = 2;
            // get user email
            getInfoNotify().then(res => {
                dataList.value = res.data.emailList.map(i => {
                    return { label: maskEmail(i), value: i };
                }) || [];
                if (res.data.emailList.length === 0) {
                    dataList.value = res.data.mobileList.map(i => {
                        return { label: maskMobile(i), value: i };
                    });
                    isEmailCase.value = false;
                }
                needEndorse.value = res.data.needEndorse;
                needEndorseDesc.value = res.data.needEndorseDesc;
                loadingCount.value -= 1;
            }).catch((err) => {
                console.log(err);
                loadingCount.value -= 1;
            });
            // 同意條款
            getAgreeTermsNotify().then(res => {
                term.value = res.data.agreeTerms;
                loadingCount.value -= 1;
            }).catch(() => {
                loadingCount.value -= 1;
            });
            // 非交易會員者要多同意此條款
            if (isNewCustom.value) {
                loadingCount.value += 1;
                getOnlineAgreeTermsNotify().then(res => {
                    termNewCustom.value = res.data.agreeTerms;
                    loadingCount.value -= 1;
                }).catch(() => {
                    loadingCount.value -= 1;
                });
            }
        };

        getData();

        // 取消申請回官網
        const cancelApply = () => {
            loadingCount.value += 1;
            store.dispatch('permissions/goFubonOfficial');
        };

        // 依照身份別打不同的同意條款api
        const postAgree = () => {
            const FN = isNewCustom.value ? postOnlineAgreeNotify : postAgreeNotify;
            return FN().then(() => {
                return true;
            });
        };

        const checkMid = () => {
            /* 開始門號核實 */
            getNeedMIDVerify().then(res => {
                if (res.data.flag) {
                    postApiMid.value = true;
                } else {
                    confirmApiMid();
                }
            });
        };

        // 同意條款api完成之後送出email
        const submitData = async () => {
            loadingCount.value = 1;
            await checkEndorseData();
            if (await postAgree()) {
                const data = {
                    email: isEmailCase.value ? selected.value : '',
                    mobile: !isEmailCase.value ? selected.value : '',
                };
                postSubmitNotify(data).then((res) => {
                    if (res.data.change) {
                        modalShow(selected.value);
                    } else {
                        goNextPage();
                    }
                }).finally(() => {
                    loadingCount.value = 0;
                });
            } else {
                loadingCount.value = 0;
            }
        };

        const checkEndorseData = () => {
            if (needEndorse.value && isEndorseAgree.value) {
                return postAgreeEndorse().then(res => res.data).catch(() => null);
            } else {
                return true;
            }
        };

        const modalShow = () => {
            $vfm.show({
                component: 'DialogBase',
                params: {
                    title: '提醒您',
                    confirmButton: '確認',
                    cancelButton: '取消',
                    content: `<h4 class="text-base">${selected.value}</h4><p class="pt-4">本次變更完成後之${caseText.value}，將一併更新至以您為要保人之所有個人險有效及停效保單。</p>`,
                },
                on: {
                    confirm() {
                        goNextPage();
                        $vfm.hide('DialogBase');
                    },
                    cancel() {
                        $vfm.hide('DialogBase');
                    }
                },
            });
        };

        const confirmApiMid = () => {
            router.push({
                name: routeName.OTP
            });
        };

        const goNextPage = () => {
            if (!isEmailCase.value) {
                checkMid();
            } else {
                router.push({
                    name: routeName.OTP
                });
            }
        };

        return {
            dataList,
            loadingCount,
            isNewCustom,
            term,
            termNewCustom,
            isScrollToBottom,
            buttonDisabled,
            selected,
            caseText,
            postApiMid,
            isValid,
            needEmailVerify,
            isEmailCase,
            submitData,
            cancelApply,
            confirmApiMid,
            isEndorseAgree,
            needEndorse,
            needEndorseDesc,
        };
    },
};
</script>
<style lang="scss" scoped>
:deep(.multiselect-option) {
    @apply break-all;
}
</style>
